import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/context/AuthContext";
import { AgeVerificationProvider } from "@/context/AgeVerificationContext";
import { ThemeProvider } from "@/context/ThemeContext";

// Pages
import Landing from "@/pages/Landing";
import Home from "@/pages/Home";
import Browse from "@/pages/Browse";
import StoryDetail from "@/pages/StoryDetail";
import Reader from "@/pages/Reader";
import Loja from "@/pages/Loja";
import AdminShop from "@/pages/AdminShop";
import { SlugGate } from "@/components/SlugGate";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Profile from "@/pages/Profile";
import AccountSettings from "@/pages/AccountSettings";
import Favorites from "@/pages/Favorites";
import History from "@/pages/History";
import AuthorDashboard from "@/pages/AuthorDashboard";
import AuthorAnalytics from "@/pages/AuthorAnalytics";
import WriteStory from "@/pages/WriteStory";
import Premium from "@/pages/Premium";
import CheckoutSuccess from "@/pages/CheckoutSuccess";
import Search from "@/pages/Search";
import AdminAds from "@/pages/AdminAds";
import Channels from "@/pages/Channels";
import ChannelDetail from "@/pages/ChannelDetail";
import Feed from "@/pages/Feed";
import AdminChannels from "@/pages/AdminChannels";
import AdminMessages from "@/pages/AdminInternalMessages";
import Mensagens from "@/pages/Mensagens";
import { MessagesBanner } from "@/components/MessagesBanner";
import Inbox from "@/pages/Inbox";
import MaisPrazer from "@/pages/MaisPrazer";
import Points from "@/pages/Points";
import AdminPoints from "@/pages/AdminPoints";
import Confessions from "@/pages/Confessions";
import SubmitConfession from "@/pages/SubmitConfession";
import ConfessionDetail from "@/pages/ConfessionDetail";
import AdminConfessions from "@/pages/AdminConfessions";
import AdminCategories from "@/pages/AdminCategories";
import AdminArticleCategories from "@/pages/AdminArticleCategories";
import AdminConfessionCategories from "@/pages/AdminConfessionCategories";
import AdminComments from "@/pages/AdminComments";
import Series from "@/pages/Series";
import SeriesDetail from "@/pages/SeriesDetail";
import MySeries from "@/pages/MySeries";
import AdminSeries from "@/pages/AdminSeries";
import AdminDashboard from "@/pages/AdminDashboard";
import AdminContentEditor from "@/pages/AdminContentEditor";
import AdminPremiumEditor from "@/pages/AdminPremiumEditor";
import AdminWooCommerce from "@/pages/AdminWooCommerce";
import AdminSubscribers from "@/pages/AdminSubscribers";
import AdminPendingStories from "@/pages/AdminPendingStories";
import AdminMaintenance from "@/pages/AdminMaintenance";
import MaintenanceGate from "@/components/MaintenanceGate";
import AdminPageManager from "@/pages/AdminPageManager";
import AdminStoryManager from "@/pages/AdminStoryManager";
import AdminArticleManager from "@/pages/AdminArticleManager";
import AdminReports from "@/pages/AdminReports";
import CustomPage from "@/pages/CustomPage";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import TermsOfService from "@/pages/TermsOfService";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import CookiePolicy from "@/pages/CookiePolicy";
import VerifyEmail from "@/pages/VerifyEmail";
import ResendVerification from "@/pages/ResendVerification";
import EmbedHero from "@/pages/EmbedHero";
import AdminEmbeds from "@/pages/AdminEmbeds";
import AdminAnalytics from "@/pages/AdminAnalytics";
import AdminExperiments from "@/pages/AdminExperiments";
import Livros from "@/pages/Livros";
import BookDetail from "@/pages/BookDetail";
import AuthorDetail from "@/pages/AuthorDetail";
import AdminLivros from "@/pages/AdminLivros";
import AdminLivrosPreview from "@/pages/AdminLivrosPreview";
import AdminHomepageHero from "@/pages/AdminHomepageHero";
import AdminMedia from "@/pages/AdminMedia";
import AuthorProfile from "@/pages/AuthorProfile";
import StoriesCatalog from "@/pages/StoriesCatalog";

// Components
import ProtectedRoute from "@/components/ProtectedRoute";
import AgeGate from "@/components/AgeGate";
import MobileBottomNav from "@/components/MobileBottomNav";
import PWAInstallPrompt from "@/components/PWAInstallPrompt";
import UpdatePrompt from "@/components/UpdatePrompt";
import ErrorBoundary from "@/components/ErrorBoundary";
import AnalyticsTracker from "@/components/AnalyticsTracker";
import CookieConsent from "@/components/CookieConsent";

// Layout wrapper that hides bottom padding on immersive pages
function AppLayoutWrapper({ children }) {
  const location = useLocation();
  const isImmersivePage = location.pathname.startsWith('/read/');
  
  return (
    <div className={isImmersivePage ? '' : 'pb-14 md:pb-0'}>
      {children}
    </div>
  );
}

function AppContent() {
  const location = useLocation();

  // Embed routes: bypass AgeGate, Navbar, MobileBottomNav, layout padding —
  // they are meant to be iframed on external sites.
  if (location.pathname.startsWith('/embed/')) {
    return (
      <Routes>
        <Route path="/embed/hero" element={<EmbedHero />} />
        <Route path="*" element={<Navigate to="/embed/hero" replace />} />
      </Routes>
    );
  }

  // Public verification/auth routes opened from emails should not be hidden
  // behind the Age-Gate (it overlays the success screen confusingly).
  const isPublicAuthRoute = [
    '/verify-email',
    '/resend-verification',
    '/forgot-password',
    '/reset-password',
  ].some((p) => location.pathname.startsWith(p));

  return (
    <>
      {!isPublicAuthRoute && <AgeGate />}
      <PWAInstallPrompt />
      <UpdatePrompt />
      <AnalyticsTracker />
      <CookieConsent />
      <MessagesBanner />
      <AppLayoutWrapper>
        <ErrorBoundary>
        <MaintenanceGate>
        <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/home" element={<Home />} />
            <Route path="/browse" element={<Browse />} />
            <Route path="/browse/:category" element={<Browse />} />
            <Route path="/story/:id" element={<SlugGate kind="story" base="/story"><StoryDetail /></SlugGate>} />
            <Route path="/read/:id" element={
              <ProtectedRoute>
                <SlugGate kind="story" base="/read"><Reader /></SlugGate>
              </ProtectedRoute>
            } />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/resend-verification" element={<ResendVerification />} />
            <Route path="/terms" element={<TermsOfService />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/cookies" element={<CookiePolicy />} />
            <Route path="/search" element={<Search />} />
            <Route path="/profile" element={
              <ProtectedRoute>
                <Profile />
              </ProtectedRoute>
            } />
            <Route path="/conta" element={
              <ProtectedRoute>
                <AccountSettings />
              </ProtectedRoute>
            } />
            <Route path="/favorites" element={
              <ProtectedRoute>
                <Favorites />
              </ProtectedRoute>
            } />
            <Route path="/history" element={
              <ProtectedRoute>
                <History />
              </ProtectedRoute>
            } />
            <Route path="/author" element={
              <ProtectedRoute>
                <AuthorDashboard />
              </ProtectedRoute>
            } />
            <Route path="/author/analytics" element={
              <ProtectedRoute>
                <AuthorAnalytics />
              </ProtectedRoute>
            } />
            <Route path="/write" element={
              <ProtectedRoute>
                <WriteStory />
              </ProtectedRoute>
            } />
            <Route path="/edit/:id" element={
              <ProtectedRoute>
                <WriteStory />
              </ProtectedRoute>
            } />
            <Route path="/premium" element={<Premium />} />
            <Route path="/checkout/success" element={
              <ProtectedRoute>
                <CheckoutSuccess />
              </ProtectedRoute>
            } />
            <Route path="/loja" element={<Loja />} />
            <Route path="/admin/shop" element={
              <ProtectedRoute>
                <AdminShop />
              </ProtectedRoute>
            } />
            <Route path="/admin/ads" element={
              <ProtectedRoute>
                <AdminAds />
              </ProtectedRoute>
            } />
            <Route path="/admin/channels" element={
              <ProtectedRoute>
                <AdminChannels />
              </ProtectedRoute>
            } />
            <Route path="/admin/messages" element={
              <ProtectedRoute>
                <AdminMessages />
              </ProtectedRoute>
            } />
            <Route path="/mensagens" element={
              <ProtectedRoute>
                <Mensagens />
              </ProtectedRoute>
            } />
            <Route path="/inbox" element={
              <ProtectedRoute>
                <Inbox />
              </ProtectedRoute>
            } />
            <Route path="/points" element={
              <ProtectedRoute>
                <Points />
              </ProtectedRoute>
            } />
            <Route path="/admin/points" element={
              <ProtectedRoute>
                <AdminPoints />
              </ProtectedRoute>
            } />
            <Route path="/channels" element={<Channels />} />
            <Route path="/channel/:id" element={<SlugGate kind="channel" base="/channel"><ChannelDetail /></SlugGate>} />
            <Route path="/prazer" element={<MaisPrazer />} />
            <Route path="/prazer/:articleId" element={<SlugGate kind="article" base="/prazer" param="articleId"><MaisPrazer /></SlugGate>} />
            <Route path="/confessions" element={<Confessions />} />
            <Route path="/confessions/:id" element={<SlugGate kind="confession" base="/confessions"><ConfessionDetail /></SlugGate>} />
            <Route path="/confess" element={
              <ProtectedRoute>
                <SubmitConfession />
              </ProtectedRoute>
            } />
            <Route path="/admin/confessions" element={
              <ProtectedRoute>
                <AdminConfessions />
              </ProtectedRoute>
            } />
            <Route path="/admin/categories" element={
              <ProtectedRoute>
                <AdminCategories />
              </ProtectedRoute>
            } />
            <Route path="/admin/confession-categories" element={
              <ProtectedRoute>
                <AdminConfessionCategories />
              </ProtectedRoute>
            } />
            <Route path="/admin/article-categories" element={
              <ProtectedRoute>
                <AdminArticleCategories />
              </ProtectedRoute>
            } />
            <Route path="/admin/comments" element={
              <ProtectedRoute>
                <AdminComments />
              </ProtectedRoute>
            } />
            <Route path="/series" element={<Series />} />
            <Route path="/series/:id" element={<SlugGate kind="series" base="/series"><SeriesDetail /></SlugGate>} />
            <Route path="/my-series" element={
              <ProtectedRoute>
                <MySeries />
              </ProtectedRoute>
            } />
            <Route path="/admin/series" element={
              <ProtectedRoute>
                <AdminSeries />
              </ProtectedRoute>
            } />
            {/* Conhece-te (Guides) — replaces the legacy /library */}
            <Route path="/conhece-te" element={
              <Series
                seriesType="guide"
                pageTitle="Conhece-te"
                pageSubtitle="Guias práticos com capítulos e PDFs descarregáveis para te conheceres melhor."
                badgeLabel="Conhece-te"
                detailRoutePrefix="/conhece-te"
                searchPlaceholder="Pesquisar guias…"
                emptyTitle="Ainda não há guias disponíveis"
                emptyTitleSearching="Nenhum guia encontrado"
              />
            } />
            <Route path="/conhece-te/:id" element={
              <SeriesDetail
                seriesType="guide"
                listRoute="/conhece-te"
                listLabel="Voltar a Conhece-te"
                badgeLabel="Conhece-te"
              />
            } />
            <Route path="/admin/conhece-te" element={
              <ProtectedRoute>
                <AdminSeries seriesType="guide" />
              </ProtectedRoute>
            } />
            {/* Backward-compat: redirect old /library URLs to the new Conhece-te area */}
            <Route path="/library" element={<Navigate to="/conhece-te" replace />} />
            <Route path="/library/:id" element={<Navigate to="/conhece-te" replace />} />
            <Route path="/my-library" element={<Navigate to="/conhece-te" replace />} />
            <Route path="/admin/library" element={<Navigate to="/admin/conhece-te" replace />} />
            <Route path="/admin" element={
              <ProtectedRoute>
                <AdminDashboard />
              </ProtectedRoute>
            } />
            <Route path="/admin/cms/content" element={
              <ProtectedRoute>
                <AdminContentEditor />
              </ProtectedRoute>
            } />
            <Route path="/admin/premium" element={
              <ProtectedRoute>
                <AdminPremiumEditor />
              </ProtectedRoute>
            } />
            <Route path="/admin/woocommerce" element={
              <ProtectedRoute>
                <AdminWooCommerce />
              </ProtectedRoute>
            } />
            <Route path="/admin/subscribers" element={
              <ProtectedRoute>
                <AdminSubscribers />
              </ProtectedRoute>
            } />
            <Route path="/admin/pending-stories" element={
              <ProtectedRoute>
                <AdminPendingStories />
              </ProtectedRoute>
            } />
            <Route path="/admin/maintenance" element={
              <ProtectedRoute>
                <AdminMaintenance />
              </ProtectedRoute>
            } />
            <Route path="/admin/cms/pages" element={
              <ProtectedRoute>
                <AdminPageManager />
              </ProtectedRoute>
            } />
            <Route path="/admin/cms/stories" element={
              <ProtectedRoute>
                <AdminStoryManager />
              </ProtectedRoute>
            } />
            <Route path="/admin/cms/articles" element={
              <ProtectedRoute>
                <AdminArticleManager />
              </ProtectedRoute>
            } />
            <Route path="/admin/reports" element={
              <ProtectedRoute>
                <AdminReports />
              </ProtectedRoute>
            } />
            <Route path="/admin/embeds" element={
              <ProtectedRoute>
                <AdminEmbeds />
              </ProtectedRoute>
            } />
            <Route path="/admin/analytics" element={
              <ProtectedRoute>
                <AdminAnalytics />
              </ProtectedRoute>
            } />
            <Route path="/admin/experiments" element={
              <ProtectedRoute>
                <AdminExperiments />
              </ProtectedRoute>
            } />

            {/* Livros — public routes (gated by section_status) */}
            <Route path="/livros" element={<Livros />} />
            <Route path="/livros/autores/:slug" element={<AuthorDetail />} />
            <Route path="/livros/:slug" element={<BookDetail />} />

            {/* Livros — admin */}
            <Route path="/admin/livros" element={
              <ProtectedRoute>
                <AdminLivros />
              </ProtectedRoute>
            } />
            <Route path="/admin/homepage-hero" element={
              <ProtectedRoute>
                <AdminHomepageHero />
              </ProtectedRoute>
            } />
            <Route path="/admin/media" element={
              <ProtectedRoute>
                <AdminMedia />
              </ProtectedRoute>
            } />
            <Route path="/admin/livros/preview/catalogue" element={
              <ProtectedRoute>
                <AdminLivrosPreview />
              </ProtectedRoute>
            } />
            <Route path="/admin/livros/preview/book/:bookId" element={
              <ProtectedRoute>
                <BookDetail previewSource="admin" />
              </ProtectedRoute>
            } />
            <Route path="/admin/livros/preview/author/:authorId" element={
              <ProtectedRoute>
                <AuthorDetail previewSource="admin" />
              </ProtectedRoute>
            } />
            <Route path="/p/:slug" element={<CustomPage />} />
            <Route path="/autor/:id" element={<AuthorProfile />} />
            <Route path="/stories/intimate" element={<StoriesCatalog kind="intimate" />} />
            <Route path="/stories/community" element={<StoriesCatalog kind="community" />} />
            <Route path="/feed" element={
              <ProtectedRoute>
                <Feed />
              </ProtectedRoute>
            } />
            <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </MaintenanceGate>
        </ErrorBoundary>
      </AppLayoutWrapper>
      <MobileBottomNav />
      <Toaster position="top-right" />
    </>
  );
}

function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <ThemeProvider>
          <AuthProvider>
            <AgeVerificationProvider>
              <AppContent />
            </AgeVerificationProvider>
          </AuthProvider>
        </ThemeProvider>
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
